/**
 * Frontend utility functions for handling multilingual content
 */
import { useTranslation } from 'react-i18next';

// Els únics idiomes que existeixen a l'aplicació. Qualsevol altra clau dins d'un
// camp multilingüe és brossa, i mostrar-la és pitjor que no mostrar res.
const IDIOMES = ['ca', 'en'];

/**
 * Un camp multilingüe pot existir i no dir res. El contingut que encara no
 * s'ha traduït es va desar com un guionet —«-»— a les missions FRONT DE
 * TEMPESTA, MIRALL i DOBLE HÈLIX: 18 hipòtesis i 41 evidències. Com que «-» és
 * una cadena no buida, el mecanisme de reserva no s'activava mai i l'alumne
 * amb la interfície en anglès veia una matriu de guionets en comptes del text
 * en català. Es tracta com el que és: contingut absent.
 */
function teContingut(valor) {
  if (typeof valor !== 'string') return false;
  const net = valor.trim();
  return net !== '' && net !== '-';
}

/**
 * Get content in the current language with fallback
 * @param {Object|string} content - Multilingual content object or string
 * @param {string} currentLanguage - Current language code (en, ca)
 * @param {string} fallbackLanguage - Fallback language code
 * @returns {string} Content in the requested language
 */
export function getLocalizedContent(content, currentLanguage = 'ca', fallbackLanguage = 'ca') {
  // If content is null or undefined, return empty string
  if (!content) return '';

  // If content is already a string (legacy format), return it
  if (typeof content === 'string') return content;

  // If content is an object, try to get the requested language
  if (typeof content === 'object' && content !== null) {
    // Try current language first
    if (teContingut(content[currentLanguage])) {
      return content[currentLanguage];
    }

    // Try fallback language
    if (teContingut(content[fallbackLanguage])) {
      return content[fallbackLanguage];
    }

    // Qualsevol dels idiomes que coneixem, i NOMÉS aquests.
    //
    // Abans aquí es retornava el primer valor de l'objecte, fos quin fos. Amb
    // una fila corrompuda del tipus {"0":"C","1":"A",...} això mostrava la
    // lletra «C» com si fos la descripció de la hipòtesi, en comptes de no
    // mostrar res i deixar clar que hi faltava contingut.
    for (const idioma of IDIOMES) {
      if (teContingut(content[idioma])) return content[idioma];
    }
  }

  // Return empty string if nothing found
  return '';
}

/**
 * Hook to get localized content using react-i18next current language
 */

export function useLocalizedContent() {
  const { i18n } = useTranslation();

  return (content) => getLocalizedContent(content, i18n.language, 'ca');
}

/**
 * Get short description with fallback to truncated full description
 * @param {Object} item - Item with short_description and description fields
 * @param {string} currentLanguage - Current language code
 * @param {number} maxLength - Maximum length for truncated description
 * @returns {string} Short description or truncated description
 */
export function getShortDescription(item, currentLanguage = 'ca', maxLength = 100) {
  if (!item) return '';

  // Try to get short description first
  const shortDesc = getLocalizedContent(item.short_description, currentLanguage);
  if (shortDesc) return shortDesc;

  // Fallback to truncated full description
  const fullDesc = getLocalizedContent(item.description, currentLanguage);
  if (fullDesc && fullDesc.length > maxLength) {
    return fullDesc.substring(0, maxLength) + '...';
  }

  return fullDesc || '';
}

/**
 * Get full description
 * @param {Object} item - Item with description field
 * @param {string} currentLanguage - Current language code
 * @returns {string} Full description
 */
export function getFullDescription(item, currentLanguage = 'ca') {
  if (!item) return '';
  return getLocalizedContent(item.description, currentLanguage);
}